import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { FaArrowLeft, FaHome, FaUserPlus, FaBookOpen, FaBars, FaTimes, FaSignInAlt, FaAddressBook } from "react-icons/fa";

import JsonLd from "../SEO/JsonLd";
import { projects } from "../../data/portfollio";

import ProjectView from "../Portfolio/ProjectView";
import ServicesSection from "./ServicesSection";

export default function Portfolio() {
  const { slug } = useParams();
  const project = projects.find((p) => p.slug === slug);
  const [galleryIndex, setGalleryIndex] = useState(null);
  const [isNavOpen, setIsNavOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const pageTitle = project ? `${project.title} — Портфолио` : "Портфолио работ — KT Мебель";

  const pageDescription = project
    ? project.description || "Проекты из портфолио преподавателя школы KT Мебель."
    : "Примеры реальных работ: модели, чертежи и паспорта для производства мебели.";

  const pageUrl = project ? `https://kt-mebel.ru/portfolio/${project.slug}` : "https://kt-mebel.ru/portfolio";

  const listJsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Портфолио проектов KT Мебель",
    url: "https://kt-mebel.ru/portfolio",
    itemListElement: projects.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      url: `https://kt-mebel.ru/portfolio/${item.slug}`,
      name: item.title,
    })),
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Главная",
        item: "https://kt-mebel.ru/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Портфолио",
        item: "https://kt-mebel.ru/portfolio",
      },
      ...(project
        ? [
            {
              "@type": "ListItem",
              position: 3,
              name: project.title,
              item: pageUrl,
            },
          ]
        : []),
    ],
  };

  const projectImages = project
    ? [
        project.preview,
        ...(project.modelImages || []),
        ...(project.measureImages || []),
        ...(project.passportImages || []),
        ...(project.clientImages || []),
      ].filter(Boolean)
    : [];

  const projectJsonLd = project
    ? {
        "@context": "https://schema.org",
        "@type": "CreativeWork",
        name: project.title,
        description: project.description || "Проект из портфолио KT Мебель.",
        url: pageUrl,
        image: projectImages.length > 0 ? projectImages : "https://kt-mebel.ru/og-image.jpg",
        inLanguage: "ru-RU",
        creator: {
          "@type": "Person",
          name: "Тарасов Евгений",
        },
        publisher: {
          "@type": "Organization",
          name: "KT Мебель",
          url: "https://kt-mebel.ru",
        },
      }
    : null;

  const galleryProjects = [
    {
      title: "Кухня с выдвижным столом",
      caption: "Современная кухня с выдвижным столом как элементом трансформации.",
      images: [
        { src: "/images/portfolio/kitchen-1.webp", alt: "Кухня, общий вид" },
        { src: "/images/portfolio/kitchen-2.webp", alt: "Кухня, общий вид с выдвижным столом" },
        { src: "/images/portfolio/kitchen-3.webp", alt: "Кухня, другой ракурс" },
        { src: "/images/portfolio/kitchen-4.webp", alt: "Кухня, пеналы под бытовую технику" },
      ],
    },
    {
      title: "Шкаф в прихожую",
      caption: "Удобная и продуманная конструкция для повседневного использования.",
      images: [{ src: "/images/portfolio/hallway-1.webp", alt: "Шкаф в прихожую, общий вид" }],
    },
    {
      title: "Спальня",
      caption:
        "Современное решение для спальни: шкаф, туалетный столик, рабочее место, стеновые панели и встроенная подсветка.",
      images: [
        { src: "/images/portfolio/bedroom-1.webp", alt: "Спальня, общий вид" },
        { src: "/images/portfolio/bedroom-2.webp", alt: "Спальня, рабочее место" },
        { src: "/images/portfolio/bedroom-3.webp", alt: "Спальня, туалетный столик" },
      ],
    },
    {
      title: "Детская комната",
      caption:
        "Современный шкаф стеллаж для детей, рабочее место, стеновые панели.",
      images: [
        { src: "/images/portfolio/children's room_1.webp", alt: "Детская комната, общий вид" },
        { src: "/images/portfolio/children's room_2.webp", alt: "Детская комната, рабочее место" },
        { src: "/images/portfolio/children's room_3.webp", alt: "Детская комната" },
        { src: "/images/portfolio/children's room_4.webp", alt: "Детская комната, другой ракурс" },
        { src: "/images/portfolio/children's room_5.webp", alt: "Детская комната, другой ракурс" },
      ],
    },
    {
      title: "Мебель для аптек",
      caption: "Проекты мебели и оборудования для разных задач и пространств.",
      images: [
        { src: "/images/portfolio/trade-1.webp", alt: "Мебель для аптек, общий вид" },
        { src: "/images/portfolio/trade-2.webp", alt: "Мебель для аптек, зона выдачи" },
        { src: "/images/portfolio/trade-3.webp", alt: "Мебель для аптек, рабочая зона" },
      ],
    },
  ];

  const galleryImages = galleryProjects.flatMap((galleryProject) =>
    galleryProject.images.map((image) => ({
      ...image,
      projectTitle: galleryProject.title,
      caption: galleryProject.caption,
    }))
  );
  const flatIndexBySrc = new Map(galleryImages.map((image, index) => [image.src, index]));

  const isGalleryOpen = galleryIndex !== null;
  const currentGalleryImage = isGalleryOpen ? galleryImages[galleryIndex] : null;

  const showPrevPhoto = () => {
    if (!isGalleryOpen) return;
    setGalleryIndex((prev) => (prev === 0 ? galleryImages.length - 1 : prev - 1));
  };

  const showNextPhoto = () => {
    if (!isGalleryOpen) return;
    setGalleryIndex((prev) => (prev === galleryImages.length - 1 ? 0 : prev + 1));
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={pageUrl} />

        <meta property="og:locale" content="ru_RU" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content="https://kt-mebel.ru/og-image.jpg" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content="https://kt-mebel.ru/og-image.jpg" />
      </Helmet>

      {!project ? <JsonLd data={listJsonLd} /> : <JsonLd data={projectJsonLd} />}
      <JsonLd data={breadcrumbJsonLd} />

      <main className="relative min-h-screen overflow-hidden bg-slate-100">
        <div className="pointer-events-none absolute -top-24 -left-20 h-72 w-72 rounded-full bg-cyan-200/45 blur-3xl" />
        <div className="pointer-events-none absolute top-40 -right-28 h-80 w-80 rounded-full bg-sky-300/35 blur-3xl" />

        <div className="max-w-[1280px] mx-auto px-4 py-10 sm:py-12 relative z-10">
          {project ? (
            <ProjectView project={project} />
          ) : (
            <>
              <header className="fixed top-0 inset-x-0 z-40 border-b border-slate-200 bg-slate-100/95 backdrop-blur-sm">
                <div className="max-w-[1280px] mx-auto px-4 h-16 flex items-center justify-between gap-3">
                  <div className="text-sm font-semibold text-slate-700">Навигация</div>

                  <nav className="hidden md:flex items-center gap-2">
                    <a
                      href="/#teacher"
                      className="inline-flex h-11 items-center justify-center gap-2 px-4 rounded-xl border border-slate-200 bg-white/95 hover:bg-white text-slate-800 font-medium transition shadow-[0_10px_24px_rgba(15,23,42,0.18)]"
                    >
                      <FaArrowLeft className="h-4 w-4" />
                      <span>Назад</span>
                    </a>
                    <a
                      href="/"
                      className="inline-flex h-11 items-center justify-center gap-2 px-4 rounded-xl border border-slate-200 bg-white/95 hover:bg-white text-slate-800 font-medium transition shadow-[0_10px_24px_rgba(15,23,42,0.18)]"
                    >
                      <FaHome className="h-4 w-4" />
                      <span>Главная</span>
                    </a>
                    <a
                      href="/#price"
                      className="inline-flex h-11 items-center justify-center gap-2 px-4 rounded-xl border border-slate-200 bg-white/95 hover:bg-white text-slate-800 font-medium transition shadow-[0_10px_24px_rgba(15,23,42,0.18)]"
                    >
                      <FaBookOpen className="h-4 w-4" />
                      <span>Программы и цены</span>
                    </a>
                    <a
                      href="/#address"
                      className="inline-flex h-11 items-center justify-center gap-2 px-4 rounded-xl border border-slate-200 bg-white/95 hover:bg-white text-slate-800 font-medium transition shadow-[0_10px_24px_rgba(15,23,42,0.18)]"
                    >
                      <FaAddressBook className="h-4 w-4" />
                      <span>Контакты</span>
                    </a>
                    <Link
                      to="/sign-in"
                      className="inline-flex h-11 items-center justify-center gap-2 px-4 rounded-xl border border-slate-200 bg-white/95 hover:bg-white text-slate-800 font-medium transition shadow-[0_10px_24px_rgba(15,23,42,0.18)]"
                    >
                      <FaSignInAlt className="h-4 w-4" />
                      <span>Вход</span>
                    </Link>
                    <Link
                      to="/sign-up"
                      className="inline-flex h-11 items-center justify-center gap-2 px-4 rounded-xl border border-slate-200 bg-white/95 hover:bg-white text-slate-800 font-medium transition shadow-[0_10px_24px_rgba(15,23,42,0.18)]"
                    >
                      <FaUserPlus className="h-4 w-4" />
                      <span>Регистрация</span>
                    </Link>
                  </nav>

                  <button
                    type="button"
                    onClick={() => setIsNavOpen((prev) => !prev)}
                    className="md:hidden inline-flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white/95 text-slate-800 shadow-[0_10px_24px_rgba(15,23,42,0.18)]"
                    aria-label={isNavOpen ? "Закрыть меню" : "Открыть меню"}
                  >
                    {isNavOpen ? <FaTimes className="h-4 w-4" /> : <FaBars className="h-4 w-4" />}
                  </button>
                </div>

                {isNavOpen && (
                  <div className="md:hidden border-t border-slate-200 bg-slate-100/95">
                    <div className="max-w-[1280px] mx-auto px-4 py-3 flex flex-col gap-2">
                      <a
                        href="/#teacher"
                        onClick={() => setIsNavOpen(false)}
                        className="inline-flex h-11 items-center justify-center gap-2 px-4 rounded-xl border border-slate-200 bg-white/95 text-slate-800 font-medium"
                      >
                        <FaArrowLeft className="h-4 w-4" />
                        <span>Назад</span>
                      </a>
                      <a
                        href="/"
                        onClick={() => setIsNavOpen(false)}
                        className="inline-flex h-11 items-center justify-center gap-2 px-4 rounded-xl border border-slate-200 bg-white/95 text-slate-800 font-medium"
                      >
                        <FaHome className="h-4 w-4" />
                        <span>Главная</span>
                      </a>
                      <a
                        href="/#price"
                        onClick={() => setIsNavOpen(false)}
                        className="inline-flex h-11 items-center justify-center gap-2 px-4 rounded-xl border border-slate-200 bg-white/95 text-slate-800 font-medium"
                      >
                        <FaBookOpen className="h-4 w-4" />
                        <span>Программы и цены</span>
                      </a>
                      <a
                        href="/#address"
                        onClick={() => setIsNavOpen(false)}
                        className="inline-flex h-11 items-center justify-center gap-2 px-4 rounded-xl border border-slate-200 bg-white/95 text-slate-800 font-medium"
                      >
                        <FaAddressBook className="h-4 w-4" />
                        <span>Контакты</span>
                      </a>
                      <Link
                        to="/sign-in"
                        onClick={() => setIsNavOpen(false)}
                        className="inline-flex h-11 items-center justify-center gap-2 px-4 rounded-xl border border-slate-200 bg-white/95 text-slate-800 font-medium"
                      >
                        <FaSignInAlt className="h-4 w-4" />
                        <span>Вход</span>
                      </Link>
                      <Link
                        to="/sign-up"
                        onClick={() => setIsNavOpen(false)}
                        className="inline-flex h-11 items-center justify-center gap-2 px-4 rounded-xl border border-slate-200 bg-white/95 text-slate-800 font-medium"
                      >
                        <FaUserPlus className="h-4 w-4" />
                        <span>Регистрация</span>
                      </Link>
                    </div>
                  </div>
                )}
              </header>

              <div className="pt-20 md:pt-24">
                <section className="mb-8 rounded-3xl border border-slate-200 bg-white/90 p-6 sm:p-8 shadow-[0_14px_30px_rgba(15,23,42,0.1)]">
                  <h1 className="text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">
                    Фото реализованных проектов
                  </h1>
                  <p className="mt-3 max-w-3xl text-slate-600">
                    Реализованы на разных производствах по моим чертежам и моделям.
                  </p>

                  <div className="mt-6 space-y-6 sm:space-y-7">
                    {galleryProjects.map((galleryProject) => (
                      <article key={galleryProject.title} className="space-y-3">
                        <div>
                          <h2 className="text-lg sm:text-xl font-semibold text-slate-900">
                            {galleryProject.title}
                          </h2>
                          <p className="text-sm sm:text-base text-slate-600">{galleryProject.caption}</p>
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                          {galleryProject.images.map((image, index) => {
                            const flatIndex = flatIndexBySrc.get(image.src);
                            return (
                              <button
                                key={`${image.src}-${index}`}
                                type="button"
                                onClick={() => setGalleryIndex(flatIndex ?? null)}
                                className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-100 shadow-sm hover:shadow-[0_14px_28px_rgba(15,23,42,0.14)] transition-all duration-300"
                              >
                                <div className="aspect-[4/3]">
                                  <img
                                    src={image.src}
                                    alt={image.alt}
                                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.05]"
                                  />
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </article>
                    ))}
                  </div>
                </section>

                <section className="mb-8 rounded-3xl border border-slate-200 bg-white p-5 sm:p-7 shadow-[0_14px_30px_rgba(15,23,42,0.1)]">
                  <div className="flex flex-col gap-3 sm:gap-4">
                    <p className="max-w-3xl text-base sm:text-2xl font-semibold leading-snug tracking-tight text-slate-900">
                      Хочешь научиться создавать такие же проекты?
                    </p>
                    <div className="flex flex-col items-start gap-2 sm:gap-2.5">
                      <Link
                        to="/sign-up"
                        className="inline-flex w-full sm:w-auto items-center justify-center px-6 py-3 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-sm sm:text-base font-semibold transition shadow-[0_10px_24px_rgba(15,23,42,0.3)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900/30"
                      >
                        РЕГИСТРИРУЙСЯ
                      </Link>
                      <span className="text-sm sm:text-base font-medium text-slate-600">
                        ПЕРВЫЕ УРОКИ БЕСПЛАТНО
                      </span>
                    </div>
                  </div>
                </section>

                <ServicesSection />
              </div>
            </>
          )}
        </div>
      </main>

      {isGalleryOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4"
          onClick={() => setGalleryIndex(null)}
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setGalleryIndex(null);
            }}
            className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/90 text-slate-800 font-bold hover:bg-white transition"
            aria-label="Закрыть"
          >
            ✕
          </button>

          {galleryImages.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  showPrevPhoto();
                }}
                className="absolute left-4 sm:left-6 h-11 w-11 rounded-full bg-white/85 text-slate-800 text-xl hover:bg-white transition"
                aria-label="Предыдущее фото"
              >
                ←
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  showNextPhoto();
                }}
                className="absolute right-4 sm:right-6 h-11 w-11 rounded-full bg-white/85 text-slate-800 text-xl hover:bg-white transition"
                aria-label="Следующее фото"
              >
                →
              </button>
            </>
          )}

          <img
            src={currentGalleryImage?.src}
            alt={currentGalleryImage?.alt || `Увеличенное фото ${galleryIndex + 1}`}
            onClick={(e) => e.stopPropagation()}
            className="max-w-[95vw] max-h-[90vh] object-contain rounded-xl bg-white"
          />

          {currentGalleryImage?.caption && (
            <div className="absolute left-1/2 -translate-x-1/2 bottom-5 max-w-[min(92vw,760px)] px-3 py-2 rounded-lg bg-slate-900/85 text-white text-sm sm:text-base text-center">
              {currentGalleryImage.projectTitle}: {currentGalleryImage.caption}
            </div>
          )}

          <div className="absolute bottom-5 right-5 px-2.5 py-1 rounded-md bg-slate-900/80 text-white text-xs">
            {galleryIndex + 1} / {galleryImages.length}
          </div>
        </div>
      )}
    </>
  );
}
